import { CHARTER_COLORS as theme } from "../../shared/controleTheme";
import { PatientControlPointCard, PatientLaunchStatusCard } from "../ClassicControlCards";
import {
    controlePatientAvatarStyle,
    controlePatientDetailHeaderStyle,
    controlePatientDetailStyle,
    controlePatientEmptyDetailStyle,
    controlePatientHeaderStyle,
    controlePatientItemWrapperStyle,
    controlePatientSidebarStyle,
    controlePatientWarningCountStyle,
    controlePatientWorkspaceStyle,
    controlePointListStyle,
    inputStyle,
    patientValidateAllPointsButtonStyle,
    patientValidateButtonStyle,
    patientValidatedButtonStyle,
} from "../classicStyles";
import { PATIENT_CONTROL_INSTRUCTIONS, PATIENT_CONTROL_POINTS, PATIENT_CONTROL_POINT_TOTAL, VARIANT } from "../classicVariant";
import { ChronicBillingPanel, PdaRemarkPanel } from "../../shared/components/PatientPanels";
import { formatPatientName, patientInitials } from "../../shared/controleFormat";
import { controlPointLabel, hasUnvalidatedPatientPoints } from "../../shared/controleVariants";
import { isControlePatientActive, isPatientControlValidated, normalizePatientPointState } from "../../shared/patientControlRules";
import {
    controlePatientCountStyle,
    controlePatientEditButtonStyle,
    controlePatientItemStyle,
    controlePatientListStyle,
    emptyPatientTextStyle,
    lastDossierButtonStyle,
} from "../../shared/styles/patientStyles";
import PatientAutocomplete from "../../../../components/PatientAutocomplete";

function validatedPointCount(values) {
    return PATIENT_CONTROL_POINTS.filter(([pointKey]) => normalizePatientPointState(values[pointKey]).validated).length;
}

function patientRowColors({ inactive, blocking, validated, active }) {
    if (inactive) return { name: theme.danger, detail: theme.danger };
    if (blocking) return { name: "#80620e", detail: "#80620e" };
    if (validated) return { name: "#0d704a", detail: "#0d704a" };
    return { name: active ? theme.primary : theme.ink, detail: theme.muted };
}

function patientRowStatus(patient, patientActive, blocking, validated) {
    if (patient.inactive) return " · Inactif";
    if (blocking) return " · À reprendre";
    return patientActive && validated ? " · Validé" : "";
}

function ClassicPatientRow({ view, patient }) {
    const { controle, editing, workspace } = view;
    const active = String(patient.patientId) === String(controle.selectedPatientId);
    const values = (workspace.formData.controlePatients || {})[patient.patientId] || {};
    const validated = isPatientControlValidated(values);
    const patientActive = isControlePatientActive(patient);
    const blocking = patientActive && hasUnvalidatedPatientPoints(values);
    const colors = patientRowColors({ inactive: patient.inactive, blocking, validated, active });
    return (
        <div
            className="controle-patient-row"
            style={controlePatientItemWrapperStyle(active, patient.inactive, validated, blocking)}
        >
            <button
                type="button"
                aria-pressed={active}
                onClick={() => controle.setSelectedPatientId(String(patient.patientId))}
                style={controlePatientItemStyle}
            >
                <span style={controlePatientAvatarStyle(active, patient.inactive, validated, blocking)}>
                    {patientInitials(patient)}
                </span>
                <span style={{ minWidth: 0, flex: 1 }}>
                    <strong style={{
                        display: "block",
                        color: colors.name,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}>
                        {formatPatientName(patient)}
                    </strong>
                    <small style={{ color: colors.detail }}>
                        {patient.chambre ? `Chambre ${patient.chambre}` : "Chambre non renseignée"}
                        {patientRowStatus(patient, patientActive, blocking, validated)}
                    </small>
                </span>
                <span style={blocking ? controlePatientWarningCountStyle : controlePatientCountStyle}>
                    {patientActive ? `${validatedPointCount(values)}/${PATIENT_CONTROL_POINT_TOTAL}` : "Inactif"}
                </span>
            </button>
            <button
                type="button"
                className="controle-patient-edit-button"
                aria-label={`Modifier ${formatPatientName(patient)}`}
                title="Modifier le patient"
                disabled={controle.actionLoading}
                onClick={() => editing.openIdentity(patient)}
                style={controlePatientEditButtonStyle}
            >
                ✎
            </button>
        </div>
    );
}

function ClassicPatientSidebar({ view }) {
    const { activeControlePatientCount, activeDossierEhpadId, controle, sortedControlePatients, workspace } = view;
    return (
        <aside style={controlePatientSidebarStyle}>
            <div style={controlePatientHeaderStyle}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px" }}>
                    <span style={{ color: theme.muted, fontSize: "12px", fontWeight: 900, textTransform: "uppercase" }}>Patients</span>
                    <strong style={{ color: theme.ink, fontSize: "13px" }}>{activeControlePatientCount}</strong>
                </div>
                <PatientAutocomplete
                    value={controle.search}
                    onChange={controle.setSearch}
                    onSelect={controle.selectSuggestion}
                    ehpadId={activeDossierEhpadId}
                    placeholder="Rechercher un patient"
                    inputStyle={{ ...inputStyle, width: "100%", height: "34px" }}
                />
            </div>
            <div style={controlePatientListStyle}>
                {controle.loading && <p style={emptyPatientTextStyle}>Chargement...</p>}
                {!controle.loading && controle.patients.length === 0 && <p style={emptyPatientTextStyle}>Aucun patient pour cet EHPAD.</p>}
                {!controle.loading && sortedControlePatients.map(patient => (
                    <ClassicPatientRow key={patient.patientId} view={view} patient={patient} />
                ))}
            </div>
            <span style={{ color: theme.muted, fontSize: "12px", padding: "0 14px 14px" }}>
                {workspace.activeDossier.ehpadNom || workspace.selectedEhpad?.nom || "EHPAD du dossier"}
            </span>
        </aside>
    );
}

function ClassicPatientActions({ view, patient, patientValues, completedCount }) {
    const { openLastDossierPatientModal, updatePatientValidation, validateAllPatientPoints } = view;
    const allPointsValidated = completedCount === PATIENT_CONTROL_POINT_TOTAL;
    const patientValidated = isPatientControlValidated(patientValues);
    return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button
                type="button"
                onClick={() => openLastDossierPatientModal(patient)}
                style={lastDossierButtonStyle}
            >
                Voir le dernier dossier de lot
            </button>
            <span style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: "62px",
                height: "32px",
                borderRadius: "999px",
                background: allPointsValidated ? "#dcfce7" : theme.primarySoft,
                color: allPointsValidated ? "#166534" : theme.primary,
                fontSize: "13px",
                fontWeight: 900,
            }}>
                {completedCount}/{PATIENT_CONTROL_POINT_TOTAL}
            </span>
            <button
                type="button"
                onClick={() => validateAllPatientPoints(patient.patientId)}
                style={patientValidateAllPointsButtonStyle}
            >
                Valider tous les points
            </button>
            <button
                type="button"
                onClick={() => updatePatientValidation(patient.patientId, !patientValidated)}
                style={patientValidated ? patientValidatedButtonStyle : patientValidateButtonStyle}
            >
                {patientValidated ? "Patient validé" : "Valider le patient"}
            </button>
        </div>
    );
}

function ClassicPatientPoints({ view, patient, patientValues }) {
    const { activeControleMode, updatePatientControl, updatePatientLaunchStatus } = view;
    return (
        <div style={controlePointListStyle}>
            {PATIENT_CONTROL_POINTS.map(([pointKey, label], index) => (
                <PatientControlPointCard
                    key={pointKey}
                    number={index + 2}
                    label={controlPointLabel(VARIANT, "avantLancement", pointKey, label, activeControleMode)}
                    instruction={PATIENT_CONTROL_INSTRUCTIONS[pointKey]}
                    value={normalizePatientPointState(patientValues[pointKey])}
                    onChange={patch => updatePatientControl(patient.patientId, pointKey, patch)}
                />
            ))}
            <PatientLaunchStatusCard
                value={normalizePatientPointState(patientValues.tracageLogiciels)}
                onChange={launchStatus => updatePatientLaunchStatus(patient.patientId, launchStatus)}
            />
        </div>
    );
}

function ClassicPatientDetail({ view, patient }) {
    const { chronic, controle, editing, workspace } = view;
    const patientActive = isControlePatientActive(patient);
    const patientValues = (workspace.formData.controlePatients || {})[patient.patientId] || {};
    const completedCount = validatedPointCount(patientValues);
    return (
        <div style={controlePatientDetailStyle}>
            <div style={controlePatientDetailHeaderStyle}>
                <div>
                    <h2 style={{ margin: 0, fontSize: "18px", color: theme.ink }}>{formatPatientName(patient)}</h2>
                    <div style={{ color: theme.muted, fontSize: "13px", marginTop: "3px" }}>
                        {patient.chambre ? `Chambre ${patient.chambre}` : "Chambre non renseignée"}
                        {patientActive ? ` · ${completedCount}/${PATIENT_CONTROL_POINT_TOTAL} points validés` : " · Patient inactif"}
                        {patientActive && isPatientControlValidated(patientValues) ? " · Patient validé" : ""}
                    </div>
                </div>
                {patientActive && (
                    <ClassicPatientActions view={view} patient={patient} patientValues={patientValues} completedCount={completedCount} />
                )}
            </div>
            {patientActive ? (
                <ClassicPatientPoints view={view} patient={patient} patientValues={patientValues} />
            ) : (
                <div style={controlePatientEmptyDetailStyle}>
                    <h2 style={{ margin: 0, fontSize: "17px", color: theme.ink }}>Patient inactif</h2>
                    <p style={{ margin: "6px 0 0", color: theme.muted, fontSize: "13px" }}>Aucun contrôle patient attendu.</p>
                </div>
            )}
            <PdaRemarkPanel controle={controle} editing={editing} patient={patient} idPrefix="controle" />
            <ChronicBillingPanel chronic={chronic} editing={editing} patient={patient} />
        </div>
    );
}

export function renderPatientControleTab(view) {
    const selectedPatient = view.controle.selectedPatient;
    return (
        <div style={controlePatientWorkspaceStyle}>
            <ClassicPatientSidebar view={view} />
            <main style={{ minWidth: 0 }}>
                {!selectedPatient ? (
                    <div style={controlePatientEmptyDetailStyle}>
                        <h2 style={{ margin: 0, fontSize: "17px", color: theme.ink }}>Aucun patient sélectionné</h2>
                        <p style={{ margin: "6px 0 0", color: theme.muted, fontSize: "13px" }}>Choisissez un patient dans la liste pour saisir ses contrôles.</p>
                    </div>
                ) : (
                    <ClassicPatientDetail view={view} patient={selectedPatient} />
                )}
            </main>
        </div>
    );
}
